import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Calendar, Phone } from 'lucide-react';
import { TinyStepsLogo } from '../components/ui/FloatingDecorations';
import FloatingHearts from '../components/ui/FloatingHearts';
import SakuraFormBg from '../components/ui/SakuraFormBg';

const COUNTRY_CODES = [
  { code: '+91', label: '+91 India', maxLen: 10, exactLen: true },
  { code: '+1',  label: '+1 US',    maxLen: 10, exactLen: false },
  { code: '+44', label: '+44 UK',   maxLen: 15, exactLen: false },
  { code: '+61', label: '+61 AU',   maxLen: 15, exactLen: false },
  { code: '+971',label: '+971 UAE', maxLen: 15, exactLen: false },
  { code: '+65', label: '+65 SG',   maxLen: 15, exactLen: false },
  { code: '+60', label: '+60 MY',   maxLen: 15, exactLen: false },
];

const SignupPage = () => {
  const [form, setForm] = useState({ name:'', nickname:'', email:'', password:'', dateOfBirth:'' });
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [dobError,   setDobError]   = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  /* ── DOB helpers ── */
  const today    = new Date(); today.setHours(0,0,0,0);
  const maxDob   = new Date(today); maxDob.setFullYear(maxDob.getFullYear() - 8);
  const minDob   = new Date(today); minDob.setFullYear(minDob.getFullYear() - 55);
  const maxDobStr = maxDob.toISOString().split('T')[0];
  const minDobStr = minDob.toISOString().split('T')[0];

  /* dobText: what user types (DD/MM/YYYY), dobIso: YYYY-MM-DD sent to backend */
  const [dobText, setDobText] = useState('');

  const calcAge = (isoDate) => {
    if (!isoDate) return null;
    const dob = new Date(isoDate + 'T00:00:00');
    const age = today.getFullYear() - dob.getFullYear()
      - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
    return age;
  };

  const validateDob = (isoDate) => {
    if (!isoDate) return 'Date of birth is required.';
    const dob = new Date(isoDate + 'T00:00:00');
    const [y, m, d] = isoDate.split('-').map(Number);
    /* real calendar date check */
    if (dob.getFullYear() !== y || dob.getMonth() + 1 !== m || dob.getDate() !== d)
      return 'Please enter a valid calendar date.';
    if (dob >= today) return 'Date of birth cannot be today or a future date.';
    const age = calcAge(isoDate);
    if (age < 8)  return 'You must be at least 8 years old to join TinySteps.';
    if (age > 55) return 'Please enter a valid date of birth (age up to 55).';
    return '';
  };

  /* Auto-format DD/MM/YYYY as user types numerics */
  const handleDobTextChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '').slice(0, 8);
    let formatted = raw;
    if (raw.length > 4) formatted = raw.slice(0,2) + '/' + raw.slice(2,4) + '/' + raw.slice(4);
    else if (raw.length > 2) formatted = raw.slice(0,2) + '/' + raw.slice(2);
    setDobText(formatted);

    /* Convert to ISO when 8 digits entered */
    if (raw.length === 8) {
      const dd = raw.slice(0,2), mm = raw.slice(2,4), yyyy = raw.slice(4);
      const iso = `${yyyy}-${mm}-${dd}`;
      setForm(f => ({ ...f, dateOfBirth: iso }));
      setDobError(validateDob(iso));
    } else {
      setForm(f => ({ ...f, dateOfBirth: '' }));
      setDobError('');
    }
  };

  /* Date picker changes (native calendar) — update both text display and iso */
  const handleDobPickerChange = (e) => {
    const iso = e.target.value; // YYYY-MM-DD
    if (!iso) { setDobText(''); setForm(f => ({ ...f, dateOfBirth: '' })); setDobError(''); return; }
    const [y, m, d] = iso.split('-');
    setDobText(`${d}/${m}/${y}`);
    setForm(f => ({ ...f, dateOfBirth: iso }));
    setDobError(validateDob(iso));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const validatePhone = (code, number) => {
    if (!number) return 'Phone number is required';
    if (!/^\d+$/.test(number)) return 'Digits only — no spaces or symbols';
    if (code === '+91') {
      if (number.length !== 10) return 'Indian mobile numbers must be exactly 10 digits';
    } else {
      if (number.length < 7 || number.length > 15) return 'Phone number must be 7–15 digits';
    }
    return '';
  };

  const handlePhoneChange = e => {
    const val = e.target.value.replace(/\D/g, '');
    setPhoneNumber(val);
    setPhoneError(validatePhone(countryCode, val));
  };

  const handleCountryCodeChange = e => {
    const newCode = e.target.value;
    setCountryCode(newCode);
    if (phoneNumber) setPhoneError(validatePhone(newCode, phoneNumber));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    const dErr = validateDob(form.dateOfBirth);
    if (dErr) { setDobError(dErr); return; }
    const pErr = validatePhone(countryCode, phoneNumber);
    if (pErr) { setPhoneError(pErr); return; }
    setLoading(true); setError('');
    const combinedPhone = countryCode + phoneNumber;
    const result = await register(form.name, form.email, form.password, form.dateOfBirth, combinedPhone, form.nickname);
    if (result.success) navigate('/onboarding');
    else setError(result.message);
    setLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-start justify-center p-4 pt-4"
      style={{ background:'linear-gradient(155deg,#FDE8F5 0%,#F5EAFF 40%,#EDE8FF 75%,#FDE8F5 100%)' }}>
      <FloatingHearts density="auth" />

      <div className="w-full max-w-md relative z-10">

        {/* Logo + Title inline */}
        <div className="flex items-center gap-3 mb-3 justify-center">
          <Link to="/" className="inline-block flex-shrink-0">
            <TinyStepsLogo size={46} />
          </Link>
          <div>
            <h1 className="text-2xl font-display font-bold text-gradient leading-tight">Begin Your Journey</h1>
            <p className="text-gray-400 text-sm font-round">Create your TinySteps account</p>
          </div>
        </div>

        {/* Card */}
        <div className="glass rounded-4xl p-5 border border-blush-100 shadow-float relative overflow-hidden">
          <SakuraFormBg />
          <div className="relative">

          {error && (
            <div className="bg-coral-50 border border-coral-200 text-coral-600 rounded-2xl px-4 py-3 mb-5 text-xs font-round">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">

            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 font-round">Your Name</label>
              <div className="flex items-center rounded-2xl border-2 border-blush-100 bg-white focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100 transition-all duration-200">
                <User className="w-4 h-4 text-blush-300 ml-4 flex-shrink-0 pointer-events-none" />
                <input type="text" name="name" value={form.name} onChange={handleChange}
                  placeholder="Your beautiful name" required
                  className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300" />
              </div>
            </div>

            {/* Nickname (optional) */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 font-round">
                Nickname (Optional) 💕
              </label>
              <p className="text-[10px] text-blush-400 font-round mb-1">What would you like TinySteps to call you?</p>
              <div className="flex items-center rounded-2xl border-2 border-blush-100 bg-white focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100 transition-all duration-200">
                <span className="ml-4 text-sm flex-shrink-0 pointer-events-none">💕</span>
                <input type="text" name="nickname" value={form.nickname} onChange={handleChange}
                  placeholder="e.g., Jaanu, Sranu, Anu"
                  maxLength={30}
                  className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300" />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 font-round">Phone Number</label>
              <div className={`flex items-center rounded-2xl border-2 bg-white transition-all duration-200 ${phoneError ? 'border-coral-300 ring-2 ring-coral-100' : 'border-blush-100 focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100'}`}>
                <Phone className="w-4 h-4 text-blush-300 ml-4 flex-shrink-0 pointer-events-none" />
                <select
                  value={countryCode}
                  onChange={handleCountryCodeChange}
                  className="pl-2 pr-1 py-2.5 bg-transparent focus:outline-none text-gray-700 text-sm font-round border-r border-blush-100 cursor-pointer"
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  inputMode="tel"
                  name="phoneNumber"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder={countryCode === '+91' ? '10-digit mobile number' : '7–15 digit number'}
                  className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300"
                />
              </div>
              {phoneError && (
                <div className="bg-coral-50 border border-coral-200 text-coral-600 rounded-2xl px-4 py-2 mt-1.5 text-xs font-round">
                  {phoneError}
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 font-round">Email Address</label>
              <div className="flex items-center rounded-2xl border-2 border-blush-100 bg-white focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100 transition-all duration-200">
                <Mail className="w-4 h-4 text-blush-300 ml-4 flex-shrink-0 pointer-events-none" />
                <input type="email" name="email" value={form.email} onChange={handleChange}
                  placeholder="your@email.com" required
                  className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300" />
              </div>
            </div>

            {/* DOB — dual mode: type OR pick */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500 font-round">Date of Birth</label>
                {form.dateOfBirth && !dobError && (
                  <span className="text-xs text-blush-400 font-round font-semibold">
                    Age {calcAge(form.dateOfBirth)} ✓
                  </span>
                )}
              </div>
              <div className={`flex items-center rounded-2xl border-2 bg-white transition-all duration-200 ${dobError ? 'border-red-300 ring-2 ring-red-100' : 'border-blush-100 focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100'}`}>
                <Calendar className={`w-4 h-4 ml-4 flex-shrink-0 pointer-events-none ${dobError ? 'text-red-300' : 'text-blush-300'}`} />
                {/* Text input for manual typing with auto-format */}
                <input
                  type="text"
                  inputMode="numeric"
                  value={dobText}
                  onChange={handleDobTextChange}
                  placeholder="DD / MM / YYYY"
                  maxLength={10}
                  className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300"
                />
                {/* Hidden native date picker — triggered by calendar icon area */}
                <div className="relative mr-3 flex-shrink-0">
                  <input
                    type="date"
                    value={form.dateOfBirth}
                    min={minDobStr}
                    max={maxDobStr}
                    onChange={handleDobPickerChange}
                    className="absolute inset-0 opacity-0 cursor-pointer w-8 h-8"
                    tabIndex={-1}
                    aria-label="Open date picker"
                  />
                  <Calendar className="w-4 h-4 text-blush-300 cursor-pointer" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-1">
                <p className="text-[10px] text-gray-400 font-round">Type DD/MM/YYYY</p>
                <p className="text-[10px] text-gray-400 font-round">Tap 📅 to pick from calendar</p>
              </div>
              {dobError && (
                <p className="text-xs text-red-500 mt-1 font-round flex items-center gap-1">
                  <span>⚠️</span> {dobError}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 font-round">Password</label>
              <div className="flex items-center rounded-2xl border-2 border-blush-100 bg-white focus-within:border-blush-300 focus-within:ring-2 focus-within:ring-blush-100 transition-all duration-200">
                <Lock className="w-4 h-4 text-blush-300 ml-4 flex-shrink-0 pointer-events-none" />
                <input type={showPass ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange}
                  placeholder="Min 6 characters" required
                  className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-gray-700 text-sm font-round placeholder-gray-300" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="mr-4 flex-shrink-0 text-gray-300 hover:text-blush-400 transition-colors">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full btn-primary py-3 text-sm font-semibold font-round disabled:opacity-60 mt-1">
              {loading
                ? <span className="flex items-center justify-center gap-2"><span className="animate-heartbeat">♥</span> Creating account...</span>
                : 'Start My Journey'}
            </button>
          </form>

          <p className="mt-4 text-center text-gray-400 text-xs font-round">
            Already have an account?{' '}
            <Link to="/login" className="text-blush-500 font-bold hover:text-blush-600 transition-colors">Sign In</Link>
          </p>
          </div>{/* end relative content wrapper */}
        </div>

        <p className="mt-4 text-center text-[10px] text-gray-300 font-round">
          By signing up you agree to our Privacy Policy. Your data is secure & private.
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
